import { useMemo, useState } from 'react'
import { LabFlow } from './LabFlow'
import {
  DemoHeader,
  InsightCard,
  OntologyResponsePanel,
  UserActionPanel,
} from './GoalDrivenDemo'
import { getPathGenerators } from '../../data/layers'
import { useT } from '../../lib/i18n'
import type { OntoPathNode } from '../../types'
import type { LabDemoProps } from './types'

const EVENT_NODE_ID = 'event-root'

// Conditions snapshot the alarm carries — fed into the path generators so
// the response chain matches the spike severity (versus the baseline).
const SPIKED_CONDITIONS = {
  H2S_pct: 18.2,
  Cl_mg_L: 18300,
  vapor_pressure_ratio: 0.42,
}

function withEventPrefix(path: OntoPathNode[], event: OntoPathNode): OntoPathNode[] {
  if (path.length === 0) return [event]
  return [
    event,
    ...path.map((n) =>
      !n.parents || n.parents.length === 0 ? { ...n, parents: [event.id] } : n,
    ),
  ]
}

export function EventDrivenDemo({ paradigm, lab, data }: LabDemoProps) {
  const t = useT()
  const demo = (data as typeof t.labDemos.eventDriven | undefined) ?? t.labDemos.eventDriven
  const [fired, setFired] = useState(false)
  const [goalId, setGoalId] = useState<'compliance' | 'uptime' | 'cost'>('compliance')

  const generators = useMemo(() => getPathGenerators(t), [t])

  const path = useMemo<OntoPathNode[]>(() => {
    if (!fired) return []
    const base = generators[goalId](SPIKED_CONDITIONS)
    const eventNode: OntoPathNode = {
      id: EVENT_NODE_ID,
      kind: 'object',
      label: demo.event.triggerNodeLabel,
      detail: demo.event.triggerNodeDetail,
    }
    return withEventPrefix(base, eventNode)
  }, [fired, generators, goalId, demo.event])

  return (
    <div className="space-y-5">
      <DemoHeader paradigm={paradigm} />

      <div className="flex flex-col gap-4">
        <UserActionPanel
          label={lab.userActionLabel}
          title={fired ? demo.activeEventLabel : demo.pendingLabel}
          hint={demo.pendingHint}
        >
          <div className="grid lg:grid-cols-[1fr_auto] gap-4 items-start">
            <EventCard event={demo.event} fired={fired} />

            <div className="flex lg:flex-col gap-2 lg:items-stretch">
              {!fired ? (
                <button
                  type="button"
                  onClick={() => setFired(true)}
                  className="mono text-[length:var(--text-micro)] uppercase tracking-widest px-3.5 py-2 rounded-[var(--radius-sm)] bg-[color:var(--color-orion-amber)] text-[color:var(--color-orion-deep)] font-medium hover:bg-[color:var(--color-orion-amber-dim)] transition-colors whitespace-nowrap"
                >
                  {demo.fireButton}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setFired(false)}
                  className="mono text-[length:var(--text-micro)] uppercase tracking-widest px-3 py-2 rounded-[var(--radius-sm)] border-faint text-[color:var(--color-orion-haze)] hover:text-[color:var(--color-orion-text)] hover:bg-[color:var(--color-orion-night)] transition-colors whitespace-nowrap"
                >
                  {demo.resetButton}
                </button>
              )}
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-[color:var(--color-orion-surface)]/40">
            <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
              <div className="flex items-baseline gap-3 flex-wrap">
                <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)]">
                  {demo.goalSelectorLabel}
                </div>
                <p
                  className="text-[color:var(--color-orion-haze)]"
                  style={{ fontSize: 'var(--text-small)' }}
                >
                  {demo.goalSelectorHint}
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {demo.goals.map((g) => {
                  const active = g.id === goalId
                  return (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => setGoalId(g.id)}
                      aria-pressed={active}
                      className={`mono text-[length:var(--text-micro)] uppercase tracking-widest px-2.5 py-1 rounded-full transition-colors ${
                        active
                          ? 'bg-[color:var(--color-orion-amber)] text-[color:var(--color-orion-deep)] font-medium'
                          : 'border-faint text-[color:var(--color-orion-haze)] hover:text-[color:var(--color-orion-text)]'
                      }`}
                    >
                      {g.label}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        </UserActionPanel>

        <OntologyResponsePanel label={lab.ontologyResponseLabel}>
          {fired ? (
            <LabFlow
              path={path}
              kindLabels={t.l3.kindLabels}
              pathKey={`event-driven::${goalId}::${fired}`}
              minHeight={360}
              maxHeight={520}
            />
          ) : (
            <EmptyState title={demo.emptyStateTitle} body={demo.emptyStateBody} />
          )}
        </OntologyResponsePanel>
      </div>

      <InsightCard title={demo.insightTitle} body={demo.insightBody} />
    </div>
  )
}

function EventCard({
  event,
  fired,
}: {
  event: {
    code: string
    meta: string
    summary: string
    descriptor: string
    payloadLabel: string
  }
  fired: boolean
}) {
  return (
    <div
      className={`rounded-[var(--radius-md)] p-4 transition-colors ${
        fired
          ? 'bg-[color:var(--color-orion-block)]/15 border border-[color:var(--color-orion-block)]/60'
          : 'border-faint bg-[color:var(--color-orion-deep)]/40'
      }`}
    >
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <span
          className={`mono text-[length:var(--text-micro)] uppercase tracking-widest ${
            fired ? 'text-[color:var(--color-orion-block)]' : 'text-[color:var(--color-orion-warn)]'
          }`}
        >
          {event.code}
        </span>
        <span className="mono text-[length:var(--text-micro)] text-[color:var(--color-orion-haze)]">
          {event.meta}
        </span>
      </div>
      <p
        className="text-[color:var(--color-orion-text)] mb-2"
        style={{ fontSize: 'var(--text-small)', lineHeight: 1.5 }}
      >
        {event.summary}
      </p>
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="mono text-[10px] uppercase tracking-widest text-[color:var(--color-orion-haze)]">
          {event.payloadLabel}
        </span>
        <span className="mono text-[length:var(--text-small)] text-[color:var(--color-orion-amber)]">
          {event.descriptor}
        </span>
      </div>
    </div>
  )
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="min-h-[260px] flex flex-col items-center justify-center text-center px-6">
      <div className="mono text-[length:var(--text-small)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-2">
        {title}
      </div>
      <p
        className="text-[color:var(--color-orion-haze)] max-w-md"
        style={{ fontSize: 'var(--text-small)', lineHeight: 1.6 }}
      >
        {body}
      </p>
    </div>
  )
}
